// src/features/steps/hooks/useStepWizard.js (FIXED VERSION)
import { useState, useEffect } from 'react';
import { useActiveContext } from '../../../shared/hooks/useActiveContext';
import { CONSTRUCTION_TYPES } from '../../../shared/utils/constants';
import useSmartStepNavigation from '../../../shared/hooks/useSmartStepNavigation';
import { validatePatternConfiguration } from '../../../shared/utils/stepDisplayUtils';
import { shouldSkipConfiguration as shouldSkipPatternConfiguration } from '../../../shared/utils/PatternCategories';
import { getStepStartingSide } from '../../../shared/utils/sideIntelligence';

export const useStepWizard = (componentIndex, editingStepIndex = null, editMode = null, mode = 'project') => {
  const { currentProject } = useActiveContext(mode);
  const [construction, setConstruction] = useState(CONSTRUCTION_TYPES.FLAT);
  const [currentStitches, setCurrentStitches] = useState(0);

  const component = currentProject?.components?.[componentIndex] || null;
  const isEditing = editingStepIndex !== null;
  const editingStep = isEditing ? component?.steps?.[editingStepIndex] : null;

  const shouldSkipConfiguration = shouldSkipPatternConfiguration;

  // Add this useEffect after the component declaration
  useEffect(() => {
    if (component?.construction) {
      setConstruction(component.construction);
    }
  }, [component?.construction]);

  // Helper function to determine starting step based on edit mode and saved stack
  const getStartingStep = () => {
    if (!editMode || !isEditing || !editingStep) {
      return 1; // Default: start at pattern selector
    }

    const savedStack = editingStep.navigationStack;
    if (!savedStack || savedStack.length === 0) {
      return 1; // Fallback if no saved stack
    }

    if (editMode === 'pattern') {
      // Find the last pattern-related screen in the saved stack
      // Pattern screens are typically 1 (PatternSelector) and 2 (PatternConfiguration)
      const lastPatternScreen = savedStack.filter(step => step <= 2).slice(-1)[0];
      const patternResult = lastPatternScreen || 1;

      return patternResult;
    }

    if (editMode === 'configuration') {
      // For configuration, go to the last screen in the saved stack
      // This will be the final config screen they were on
      const configResult = savedStack[savedStack.length - 1];

      return configResult;
    }

    console.log('🔧 Starting Step: 1 (fallback)');
    return 1; // Fallback
  };

  const startingStep = getStartingStep();


  // Helper function to get initial wizard data
  const getInitialWizardData = (defaultUnits = 'inches') => ({
    stitchPattern: {
      category: null,
      pattern: null,
      customText: '',
      method: '',
      rowsInPattern: '',
      stitchCount: '',
      customDetails: '',
      entryMode: 'row_by_row',
      rowInstructions: []
    },
    duration: { type: '', value: '', units: defaultUnits, measurement: '', targetLength: '' },
    hasShaping: false,
    shapingConfig: {},
    prepNote: ''
  });

  const [wizardData, setWizardData] = useState(() => {
    // 🔧 FIX: Always start with complete initial data structure
    const initialData = getInitialWizardData(currentProject?.defaultUnits);

    if (isEditing && editingStep?.wizardConfig) {
      // ✅ FIX: Deep merge saved config with initial structure to ensure all fields exist
      return {
        ...initialData,
        ...editingStep.wizardConfig,
        stitchPattern: {
          ...initialData.stitchPattern,
          ...editingStep.wizardConfig.stitchPattern
        },
        duration: {
          ...initialData.duration,
          ...editingStep.wizardConfig.duration
        },
        hasShaping: editingStep.advancedWizardConfig?.hasShaping || editingStep.wizardConfig.hasShaping || false,
        shapingConfig: {
          ...initialData.shapingConfig,
          ...(editingStep.advancedWizardConfig?.shapingConfig || editingStep.wizardConfig.shapingConfig || {})
        },
        prepNote: editingStep.prepNote || editingStep.wizardConfig.prepNote || ''
      };
    }

    // Creating new step - check for component defaults
    if (component?.defaultPattern) {
      initialData.stitchPattern = {
        ...initialData.stitchPattern,
        ...JSON.parse(JSON.stringify(component.defaultPattern))
      };
    }

    // Apply component colorwork default if exists
    if (component?.defaultColorwork) {
      initialData.colorwork = JSON.parse(JSON.stringify(component.defaultColorwork));
    } else if (component?.colorMode === 'single' && component?.singleColorLetter) {
      initialData.colorwork = {
        type: 'single',
        letter: component.singleColorLetter
      };
    }

    return initialData;
  });

  // Initialize smart navigation with saved data if editing
  const smartNav = useSmartStepNavigation(
    startingStep,
    wizardData,
    (section, data) => { updateWizardData(section, data); },
    // ✅ NEW: Pass saved navigation data if editing
    isEditing ? {
      savedStack: editingStep?.navigationStack,
      savedCache: editingStep?.navigationCache
    } : null
  );

  // Inherit construction from previous step
  // Get current stitch count from the step chain
  useEffect(() => {

    if (!component) return;

    // Special handling for notepad mode
    if (mode === 'notepad' || mode === 'note') {
      if (component.steps.length === 0) {
        // For notes, use the component's startingStitches (which came from note mapping)
        const noteStartingStitches = component.startingStitches || 0;

        setCurrentStitches(noteStartingStitches);
        return;
      }
    } else {
      // ✅ FIX: Original project logic - check component.startingStitches!
      if (component.steps.length === 0) {
        // No steps yet - use component's starting stitches (not 0!)
        const initialStitches = component.startingStitches || 0;
        setCurrentStitches(initialStitches);
        return;
      }
    }

    // Common logic for both modes: get stitches from last step
    const lastStep = component.steps[component.steps.length - 1];
    const stitchCount = lastStep.endingStitches || lastStep.expectedStitches || 0;
    setCurrentStitches(stitchCount);

  }, [component?.steps, componentIndex, mode, component?.startingStitches]);

  const updateWizardData = (sectionOrKey, dataOrValue) => {

    setWizardData(prev => {
      // Handle root-level properties (like hasShaping, prepNote)
      if (typeof dataOrValue === 'boolean' || typeof dataOrValue === 'string' || typeof dataOrValue === 'number') {
        return {
          ...prev,
          [sectionOrKey]: dataOrValue
        };
      }

      // Handle nested sections (like stitchPattern, duration, shapingConfig)
      return {
        ...prev,
        [sectionOrKey]: { ...prev[sectionOrKey], ...dataOrValue }
      };
    });
  };

  // Reset wizard data function
  const resetWizardData = () => {
    setWizardData(getInitialWizardData(currentProject?.defaultUnits));
    smartNav.clearCache(); // Clear navigation cache too
  };

  // Check if current pattern can have shaping
  const canHaveShaping = () => {
    const { pattern } = wizardData.stitchPattern;
    return pattern && pattern !== 'Cast On' && pattern !== 'Bind Off';
  };

  // 🎯 NEW: Smart Step Flow Logic
  // These functions determine the actual flow based on wizard data
  const getNextStep = (currentStep) => {
    const { pattern } = wizardData.stitchPattern;

    switch (currentStep) {
      case 1: // PatternSelector
        // Check if we should skip configuration
        if (shouldSkipConfiguration(wizardData)) {
          return 3; // Skip directly to Duration/Shaping choice
        }
        return 2; // Go to configuration

      case 2: // PatternConfiguration  
        // Cast On and Bind Off patterns skip to preview
        if (pattern === 'Cast On' && wizardData.stitchPattern.stitchCount) {
          return 5; // Skip to preview
        }
        if (pattern === 'Bind Off') {
          return 5; // Skip to preview
        }
        return 3; // Go to Duration/Shaping choice

      case 3: // Duration/Shaping Choice
        if (wizardData.hasShaping === false) {
          return 4; // Go to duration config
        }
        return 5; // Go to preview (or future shaping step)

      case 4: // Duration Config
        return 5; // Go to preview

      default:
        return currentStep + 1;
    }
  };



  // 🎯 NEW: Enhanced Validation Logic
  const canProceed = (step) => {
    switch (step) {
      case 1: // PatternSelector
        return wizardData.stitchPattern.category && wizardData.stitchPattern.pattern;

      case 2: // PatternConfiguration
        if (shouldSkipConfiguration(wizardData)) {
          return true; // Skip validation for basic patterns
        }


        const validationResult = validatePatternConfiguration(wizardData.stitchPattern);

        // Use centralized validation from stepDisplayUtils
        return validationResult;

      case 3: // Duration/Shaping choice
        return true; // Choice steps handle their own advancement

      case 4: // Duration Config
        if (wizardData.stitchPattern.pattern === 'Bind Off') {
          return true;
        }
        const { type, value } = wizardData.duration || {};
        return type && value;

      case 5: // Preview
        return true;

      default:
        return false;
    }
  };

  // 🎯 NEW: Enhanced Navigation Object
  const navigation = {
    canProceed: () => canProceed(smartNav.currentStep),

    nextStep: () => {

      const nextStep = getNextStep(smartNav.currentStep);
      smartNav.goToStep(nextStep);
    },

    previousStep: () => {
      const result = smartNav.goBack();

      if (result.action === 'exit') {
        // Will be handled by parent component
        return { shouldExit: true };
      }
      return result;
    },

    goToStep: (step) => {
      smartNav.goToStep(step);
    },

    // 🎯 NEW: Enhanced navigation methods
    goToStepWithCycle: (step, cycleType) => {
      smartNav.goToStep(step, { isCycleEntry: true, cycleType });
    },

    handleBackWithUnsavedChanges: () => {
      // Integration point for unsaved changes modal
      if (smartNav.hasUnsavedChanges()) {
        return { shouldPrompt: true, changes: 'wizard-data' };
      }
      return smartNav.goBack();
    }
  };

  // 🎯 NEW: Debug information
  const getDebugInfo = () => {
    return {
      ...smartNav.getNavigationContext(),
      wizardData: JSON.stringify(wizardData, null, 2),
      canProceed: canProceed(smartNav.currentStep)
    };
  };

  // ✅ NEW: Get starting side for the step being created/edited
  const getStepStartingSideForWizard = () => {
    if (!component) return 'RS';

    // If editing an existing step, use its starting side
    if (isEditing && editingStep?.sideTracking?.startingSide) {
      return editingStep.sideTracking.startingSide;
    }

    // For new steps, calculate based on component's steps
    const stepIndex = component.steps?.length || 0;
    return getStepStartingSide(component, stepIndex);
  };

  return {
    // State (updated to use smart navigation)
    wizardStep: smartNav.currentStep,
    wizardData,
    construction,
    currentStitches,
    component,
    componentIndex,
    isEditing,
    editingStepIndex,
    editingStep,

    // Actions
    updateWizardData,
    setConstruction,
    canHaveShaping,
    resetWizardData,
    setCurrentStitches,
    getStepStartingSide: getStepStartingSideForWizard,

    // Navigation (enhanced)
    navigation,

    // Smart history for Edit
    navigationStack: smartNav.navigationStack,
    navigationCache: smartNav.dataCache,

    // 🎯 NEW: Smart navigation utilities
    smartNavigation: {
      canGoBack: smartNav.canGoBack,
      clearCache: smartNav.clearCache,
      getDebugInfo,
      hasUnsavedChanges: smartNav.hasUnsavedChanges
    }
  };
};

export default useStepWizard;